import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';

import { FilterBar, TitleListGraphql } from '@/lazy-load-components';

import ContentHeader from '@/components/content-header/ContentHeader.vue';

import { CollectionType } from '@/enums/collection-type';
import { TitleOrder } from '@/enums/title-order';
import { TitleListLayout } from '@/enums/title-list-layout';
import { PopularTitlesSorting, TitleFilter, SraPlacement } from '@/@types/graphql-types';

import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { TitleEventPayloadGraphql, trackTitleEventGraphql } from '@/helpers/title-helper';
import { toGraphqlFilters } from '@/helpers/graphql-model-helper';

import type { FilterName } from '@/stores/filter.store';
import {
	GetSearchTitlesDocument,
	GetSearchTitlesQuery,
	GetSearchTitlesQueryVariables,
} from '@/pages/graphql/queries/GetSearchTitles.query';

import type TitleListGraphqlType from '@/components/list/TitleListGraphql.vue';
import SponsoredRecommendation from '@/components/sponsored-recommendations/SponsoredRecommendation.vue';
import { toastError } from '@/helpers/toast/toast-helper';
import { useSendBidId } from '@/components/sponsored-recommendations/useSendBidId';
// import SearchFeatureQualityTvRow from '@/components/search/QualityTv/SearchFeatureQualityTvRow.vue';
// import { allowedCountriesQualityTvSearchFeature } from '@/components/search/QualityTv';
import { useFilterStore, useLanguageStore, useRoutingStore, useUserStore } from '@/helpers/composables/useStores';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery } from '@/helpers/composables/useApollo';


export default /*#__PURE__*/_defineComponent({
  __name: 'Search',
  setup(__props) {

const FilterBarSeo = () =>
	import(/* webpackChunkName: "filter-bar-seo" */ '@/components/filter-bar/seo/FilterBarSeo.vue');
const TitleListOptionsSeo = () =>
	import(/* webpackChunkName: "title-list-options-seo" */ '@/components/filter-bar/seo/TitleListOptionsSeo.vue');

const { resetFilters, filters, selectedProviders } = useFilterStore();
const filtersSearch = computed(() => filters.value(CollectionType.SEARCH));

const { language, country } = useLanguageStore();

const { queryParams, activeRoute, lastRoute } = useRoutingStore();

const { isLoggedIn } = useUserStore();

const isSearchView = ref(activeRoute.value.name === 'app.search.list');
const isFirstPageLoaded = ref(false);
const listLayout = ref<TitleListLayout>(TitleListLayout.ROW);

const showFooter = ref(false);

const isSearchCollection = computed(() => activeRoute.value.meta?.collectionType === CollectionType.SEARCH);

const query = ref(activeRoute.value.query?.q ?? '');
const isFiltersVisible = ref(false);

const titleList = ref<InstanceType<typeof TitleListGraphqlType> | null>(null);

/* We need a way to watch for filter changes AND query changes, since q and person_id parameters are not tracked in any store.*/
watch([isFiltersVisible, query, () => filtersSearch.value], () => {
	titleList.value?.resetInfiniteScroll();

	isFirstPageLoaded.value = false;

	// scroll to top if the filters have changed
	window.scrollTo(0, 0);
});

const graphqlFilters = computed<TitleFilter>(() =>
	toGraphqlFilters(filtersSearch.value, selectedProviders.value(!isLoggedIn.value))
);

const personId = computed(() => (activeRoute.value.query.person_id ? `tp${activeRoute.value.query.person_id}` : null));

// Sort By options from GraphQL are Uppercase, but tracking expects lowercase TODO [Graphql] remove when get rid of REST
const RestToGraphQLSortingMap: Partial<{ [key in TitleOrder]: PopularTitlesSorting }> = {
	[TitleOrder.POPULARITY]: PopularTitlesSorting.Popular,
	[TitleOrder.TRENDING]: PopularTitlesSorting.Trending,
	[TitleOrder.TMDB_POPULARITY]: PopularTitlesSorting.TmdbPopularity,
	[TitleOrder.ALPHABETICAL]: PopularTitlesSorting.Alphabetical,
	[TitleOrder.RANDOM]: PopularTitlesSorting.Random,
	[TitleOrder.RELEASE_YEAR]: PopularTitlesSorting.ReleaseYear,
	[TitleOrder.RELEVANCE]: PopularTitlesSorting.Relevance,
	[TitleOrder.SCORE_IMDB]: PopularTitlesSorting.ImdbScore,
	[TitleOrder.RUNTIME]: PopularTitlesSorting.Runtime,
};
const sortBy = computed<PopularTitlesSorting>(() => {
	// To fetch titles on server side we need to get sort by option from url params
	if (process.server) {
		const currentSortBy = queryParams.value.sort_by as TitleOrder;
		return RestToGraphQLSortingMap[currentSortBy] ?? PopularTitlesSorting.Popular;
	}

	if (filtersSearch.value.sort_by) {
		return RestToGraphQLSortingMap[filtersSearch.value.sort_by as TitleOrder] ?? PopularTitlesSorting.Popular;
	}

	return PopularTitlesSorting.Popular;
});

const randomSeed = computed(() => {
	const isRandomSort = sortBy.value === PopularTitlesSorting.Random;

	if (process.server) {
		const randomSeed = Number(queryParams.value.sorting_random_seed) || 1;
		return isRandomSort ? randomSeed : 0;
	}

	const randomSeed = filtersSearch.value.sorting_random_seed || 1;
	return isRandomSort ? randomSeed : 0;
});

const queryVariables = computed<GetSearchTitlesQueryVariables>(() => ({
	searchAfterCursor: '',
	searchTitlesFilter: {
		...(isFiltersVisible.value || isLoggedIn.value ? graphqlFilters.value : {}),
		searchQuery: personId.value ? null : query.value,
		personId: personId.value ?? null,
		includeTitlesWithoutUrl: true,
	},
	searchTitlesSortBy: sortBy.value,
	language: language.value,
	country: country.value,
	sortRandomSeed: randomSeed.value,
}));

const {
	result: searchPageData,
	loading: isTitlesLoading,
	fetchMore,
	onError,
	onResult,
} = useQuery<GetSearchTitlesQuery, GetSearchTitlesQueryVariables>(GetSearchTitlesDocument, queryVariables);

onResult(() => (isFirstPageLoaded.value = true));

onError(error => {
	captureMessageForSentry('[GraphQL Get Search Page Data]:', { error, where: 'Component: Search' }, 'error');
	if (process.client) showErrorToast();
});

function onLoadNext({ event }: { event: any }) {
	if (event == null) return;

	const searchTitlesFetchMore = fetchMore({
		variables: {
			searchAfterCursor: searchPageData.value?.popularTitles.pageInfo.endCursor,
		},
		updateQuery(previousResult, { fetchMoreResult }) {
			if (!fetchMoreResult) return previousResult;

			const previousEdges = previousResult.popularTitles.edges ?? [];
			const fetchMoreEdges = fetchMoreResult.popularTitles.edges ?? [];

			return {
				popularTitles: {
					__typename: previousResult?.popularTitles.__typename,
					totalCount: fetchMoreResult?.popularTitles.totalCount,
					edges: [...previousEdges, ...fetchMoreEdges],
					pageInfo: fetchMoreResult?.popularTitles.pageInfo,
					sponsoredAd: previousResult?.popularTitles.sponsoredAd,
				},
			};
		},
	});

	if (searchTitlesFetchMore == null) return;
	searchTitlesFetchMore
		.catch(error => {
			captureMessageForSentry(
				'[GraphQL Search Page Load More Titles Issue]:',
				{ error, where: 'Component: Search, method: onLoadNext' },
				'error'
			);
		})
		.finally(() => event.target?.complete?.(searchTitles.value.length));
}

const searchTitles = computed(() => searchPageData.value?.popularTitles.edges ?? []);

const titleAmount = computed(() => searchPageData.value?.popularTitles.totalCount ?? 0);

const isFirstPage = computed(() => !searchPageData.value?.popularTitles.pageInfo?.hasPreviousPage);

const isAtEndOfList = computed(() => !searchPageData.value?.popularTitles.pageInfo?.hasNextPage);

const personBackdrop = computed(() => {
	const titleWithBackdrop = searchTitles.value.find(edge => edge.node.content?.backdrops?.[0]?.backdropUrl);

	if (titleWithBackdrop) {
		return titleWithBackdrop.node.content.backdrops[0].backdropUrl;
	}

	return null;
});
const firstSearchTitle = computed(() => searchTitles.value[0]?.node ?? null);

// Pausing the Search QTV
// QUALITY TV FEATURE
// const showQualityTV = computed(
// 	() => false && allowedCountriesQualityTvSearchFeature.includes(country.value) && !isLoggedIn.value
// );

// const isSearchResultEmpty = computed(() => searchTitles.value.length === 0);

// const queryTitle = computed(() =>
// 	!isSearchResultEmpty.value && firstSearchTitle.value != null ? firstSearchTitle.value.content.title : ''
// );

// QUALITY TV FEATURE

watch(
	() => activeRoute.value,
	() => {
		if (isSearchCollection.value) {
			if (lastRoute.value?.meta?.collectionType !== CollectionType.SEARCH) {
				resetFilters({
					filterNames: Object.keys(filtersSearch.value) as FilterName[],
					updateRouteState: false,
				});
			}

			query.value = activeRoute.value.query.q as string;
		}
	}
);

// Sponsored Recommendation
const { sponsoredAd, isSRLoaded } = useSendBidId({
	pageType: 'VIEW_SEARCH',
	placement: SraPlacement.SearchPage,
	isEnabled: () => isFirstPageLoaded && firstSearchTitle.value?.id != null,
	jwEntityID: () => firstSearchTitle.value?.id,
});

function trackClick(payload: TitleEventPayloadGraphql) {
	trackTitleEventGraphql('title_clicked', payload, 'searchsuggester');
}

const { t } = useI18n();
function showErrorToast() {
	toastError({ message: t('WEBAPP_ALERT_ERROR_TITLE') as string, duration: 2000 });
}

return { __sfc: true,FilterBarSeo, TitleListOptionsSeo, resetFilters, filters, selectedProviders, filtersSearch, language, country, queryParams, activeRoute, lastRoute, isLoggedIn, isSearchView, isFirstPageLoaded, listLayout, showFooter, isSearchCollection, query, isFiltersVisible, titleList, graphqlFilters, personId, RestToGraphQLSortingMap, sortBy, randomSeed, queryVariables, searchPageData, isTitlesLoading, fetchMore, onError, onResult, onLoadNext, searchTitles, titleAmount, isFirstPage, isAtEndOfList, personBackdrop, firstSearchTitle, sponsoredAd, isSRLoaded, trackClick, t, showErrorToast, FilterBar, TitleListGraphql, ContentHeader, CollectionType, SponsoredRecommendation }
}

})